/**
 * Price Display Component - Volume Discount Mode
 * Handles dynamic price display with volume discounts based on quantity
 * 
 * Discount tiers:
 * - 1 box: 0% discount
 * - 2-3 boxes: 10% discount
 * - 4+ boxes: 20% discount
 */

(function() {
  'use strict';

  const PriceDisplay = {
    // Configuration
    config: {
      selectors: {
        priceContainer: '.price-display--volume',
        originalPrice: '.price-display__original',
        originalValue: '.price-display__original-value',
        currentValue: '.price-display__current-value',
        badge: '.price-display__badge',
        discountPercent: '.js-discount-percent',
        quantityInput: '.quantity__input',
        addToCartButton: '.product-form__submit',
        addToCartButtonText: '.product-form__submit > span:first-child',
        totalPriceDisplay: '.js-add-to-cart-total'
      }
    },

    // State
    state: {
      originalPrice: 0,
      discountTiers: [],
      currentQuantity: 1,
      currency: 'EUR',
      sectionId: null
    },

    /**
     * Initialize the component
     */
    init: function() {
      const container = document.querySelector(this.config.selectors.priceContainer);
      if (!container) return;

      // Parse data from DOM
      this.state.originalPrice = parseFloat(container.dataset.originalPrice) || 0;
      this.state.currency = container.dataset.currency || 'EUR';
      this.state.sectionId = container.dataset.sectionId;
      
      try {
        this.state.discountTiers = JSON.parse(container.dataset.discountTiers || '[]');
      } catch (e) {
        console.error('PriceDisplay: Failed to parse discount tiers', e);
        this.state.discountTiers = [];
      }

      // Read initial quantity from input
      const quantityInput = document.querySelector(this.config.selectors.quantityInput);
      if (quantityInput) {
        this.state.currentQuantity = parseInt(quantityInput.value) || 1;
      }

      // Bind event listeners
      this.bindEvents();
      
      // Initial update
      this.updatePriceDisplay();
    },

    /**
     * Bind event listeners
     */
    bindEvents: function() {
      const self = this;

      // Listen for quantity changes
      const quantityInput = document.querySelector(this.config.selectors.quantityInput);
      if (quantityInput) {
        quantityInput.addEventListener('change', function(e) {
          self.state.currentQuantity = parseInt(e.target.value) || 1;
          self.updatePriceDisplay();
        });

        // Also listen for input events for real-time updates
        quantityInput.addEventListener('input', function(e) {
          self.state.currentQuantity = parseInt(e.target.value) || 1;
          self.updatePriceDisplay();
        });
      }

      // Listen for quantity button clicks (plus/minus)
      document.addEventListener('click', function(e) {
        const button = e.target.closest('.quantity__button');
        if (button) {
          // Small delay to let the input value update
          setTimeout(function() {
            const input = document.querySelector(self.config.selectors.quantityInput);
            if (input) {
              self.state.currentQuantity = parseInt(input.value) || 1;
              self.updatePriceDisplay();
            }
          }, 50);
        }
      });

      // Listen for variant changes
      document.addEventListener('variant:changed', function(e) {
        if (e.detail && e.detail.variant) {
          self.state.originalPrice = e.detail.variant.price;
          self.updatePriceDisplay();
        }
      });

      // Also listen for Shopify's native variant change events
      const productForm = document.querySelector('product-form');
      if (productForm) {
        const observer = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-variant-id') {
              // Variant changed, try to get new price
              self.fetchCurrentVariantPrice();
            }
          });
        });
        
        const variantInput = productForm.querySelector('.product-variant-id');
        if (variantInput) {
          observer.observe(variantInput, { attributes: true });
        }
      }
    },

    /**
     * Fetch current variant price from the page
     */
    fetchCurrentVariantPrice: function() {
      const priceElement = document.querySelector('.price-item--regular');
      if (priceElement) {
        // Try to extract price from text - this is a fallback
        const priceText = priceElement.textContent;
        const priceMatch = priceText.match(/[\d.,]+/);
        if (priceMatch) {
          // Convert European format (26,90) to number
          const price = parseFloat(priceMatch[0].replace(',', '.')) * 100;
          if (!isNaN(price)) {
            this.state.originalPrice = price;
            this.updatePriceDisplay();
          }
        }
      }
    },

    /**
     * Get discount percentage based on quantity
     */
    getDiscountPercent: function(quantity) {
      let discount = 0;
      
      // Sort tiers by min quantity (descending) to find the highest applicable tier
      const sortedTiers = [...this.state.discountTiers].sort((a, b) => b.min - a.min);
      
      for (const tier of sortedTiers) {
        if (quantity >= tier.min) {
          discount = tier.discount;
          break;
        }
      }
      
      return discount;
    },

    /**
     * Format price in currency
     */
    formatPrice: function(priceInCents) {
      const price = priceInCents / 100;
      
      // Format based on currency
      if (this.state.currency === 'EUR') {
        return '€' + price.toFixed(2).replace('.', ',');
      }
      
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.state.currency
      }).format(price);
    },

    /**
     * Update the price display
     */
    updatePriceDisplay: function() {
      const quantity = this.state.currentQuantity;
      const originalPrice = this.state.originalPrice;
      const discountPercent = this.getDiscountPercent(quantity);
      
      // Calculate discounted price per unit
      const discountMultiplier = 1 - (discountPercent / 100);
      const discountedPricePerUnit = Math.round(originalPrice * discountMultiplier);
      
      // Calculate total price
      const totalPrice = discountedPricePerUnit * quantity;
      
      // Update DOM elements
      const originalPriceEl = document.querySelector(this.config.selectors.originalPrice);
      const originalValueEl = document.querySelector(this.config.selectors.originalValue);
      const currentValueEl = document.querySelector(this.config.selectors.currentValue);
      const badgeEl = document.querySelector(this.config.selectors.badge);
      const discountPercentEl = document.querySelector(this.config.selectors.discountPercent);

      // Show/hide original price with strikethrough
      if (originalPriceEl) {
        if (discountPercent > 0) {
          originalPriceEl.style.display = '';
          if (originalValueEl) {
            originalValueEl.textContent = this.formatPrice(originalPrice);
          }
        } else {
          originalPriceEl.style.display = 'none';
        }
      }

      // Update current/discounted price
      if (currentValueEl) {
        currentValueEl.textContent = this.formatPrice(discountedPricePerUnit);
      }

      // Show/hide discount badge
      if (badgeEl) {
        if (discountPercent > 0) {
          badgeEl.style.display = '';
          if (discountPercentEl) {
            discountPercentEl.textContent = discountPercent;
          }
        } else {
          badgeEl.style.display = 'none';
        }
      }

      // Update add to cart button with total price
      this.updateAddToCartButton(totalPrice);
    },

    /**
     * Update the add to cart button to show total price
     */
    updateAddToCartButton: function(totalPrice) {
      const button = document.querySelector(this.config.selectors.addToCartButton);
      const buttonTextEl = document.querySelector(this.config.selectors.addToCartButtonText);
      
      if (!button || !buttonTextEl) return;
      
      // Check if button is disabled (sold out/unavailable)
      if (button.disabled) return;

      // Get the add to cart text from translations or default
      const addToCartText = window.priceDisplayTranslations?.addToCart || 'Add to cart';
      
      // Update button text with total price
      const formattedPrice = this.formatPrice(totalPrice);
      buttonTextEl.textContent = `${addToCartText} - ${formattedPrice}`;
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      PriceDisplay.init();
    });
  } else {
    PriceDisplay.init();
  }

  // Re-initialize when section is reloaded (Shopify theme editor)
  document.addEventListener('shopify:section:load', function() {
    PriceDisplay.init();
  });

  // Export for external use
  window.PriceDisplay = PriceDisplay;
})();

